import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Minus, Maximize2, X } from "lucide-react";
import { useBatchUploadProgress } from "../../context/BatchUploadProgressContext";
import { usePopupStackContainer } from "../../context/PopupStackContext";
import { BATCHES } from "../../routes/routes";

const PopupShell = styled.div`
  position: relative;
  width: ${(p) => (p.$minimized ? "auto" : "320px")};
  max-width: ${(p) => (p.$minimized ? "min(280px, calc(100vw - 32px))" : "320px")};
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: ${(p) => (p.$minimized ? "999px" : "12px")};
  box-shadow: ${(p) =>
    p.$minimized
      ? "0 4px 20px rgba(0, 0, 0, 0.18)"
      : "0 16px 40px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.04)"};
  overflow: hidden;
  user-select: none;
  pointer-events: auto;
  transition: border-radius 0.2s ease, box-shadow 0.2s ease;
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: ${(p) => (p.$minimized ? "0.4rem 0.65rem" : "0.5rem 0.6rem")};
  background: #f9fafb;
  border-bottom: ${(p) => (p.$minimized ? "none" : "1px solid #e5e7eb")};
  cursor: ${(p) => (p.$minimized ? "pointer" : "default")};

  &:focus {
    outline: none;
  }
`;

const HeaderTitle = styled.span`
  flex: 1;
  font-size: 0.8rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const MinimizedProgressTrack = styled.div`
  height: 3px;
  background: #e5e7eb;
  width: 100%;
`;

const MinimizedProgressFill = styled.div`
  height: 100%;
  width: ${(p) => p.$width}%;
  background: ${(p) =>
    p.$failed ? "#ef4444" : p.$complete ? "#10b981" : "#3b82f6"};
  transition: width 0.25s ease;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;

  svg {
    stroke: currentColor;
    flex-shrink: 0;
  }

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }

  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
  }
`;

const PopupBody = styled.div`
  padding: 0.75rem;
`;

const JobBlock = styled.div`
  & + & {
    margin-top: 0.85rem;
    padding-top: 0.85rem;
    border-top: 1px solid #f3f4f6;
  }
`;

const JobName = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const JobMeta = styled.div`
  font-size: 0.72rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const ProgressTrack = styled.div`
  height: 8px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(p) => p.$width}%;
  background: ${(p) =>
    p.$failed ? "#ef4444" : p.$complete ? "#10b981" : "#3b82f6"};
  transition: width 0.25s ease;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.6rem;
`;

const TextButton = styled.button`
  flex: 1;
  font-size: 0.72rem;
  padding: 0.35rem 0.5rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }

  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
  }
`;

const SuccessMessage = styled.div`
  font-size: 0.72rem;
  color: #047857;
  line-height: 1.45;
`;

const SuccessLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: #2563eb;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #1d4ed8;
  }

  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
  }
`;

function isJobComplete(job) {
  return job.status === "completed" || job.status === "complete";
}

function getAggregateProgress(jobs) {
  const active = jobs.filter(
    (j) => !isJobComplete(j) && j.status !== "failed"
  );
  if (active.length === 0) {
    return jobs.some(isJobComplete) ? 100 : 0;
  }
  const sum = active.reduce((acc, j) => acc + (j.progress || 0), 0);
  return Math.round(sum / active.length);
}

function buildMinimizedLabel(jobs, activeCount) {
  const progress = getAggregateProgress(jobs);

  if (activeCount > 0) {
    if (jobs.length === 1) {
      return `Uploading · ${jobs[0].progress ?? progress}%`;
    }
    return `Uploading · ${progress}% (${activeCount}/${jobs.length})`;
  }

  if (jobs.every(isJobComplete)) {
    return `Upload complete · 100%`;
  }

  if (jobs.some((j) => j.status === "failed")) {
    return `Upload failed`;
  }

  return `Uploads (${jobs.length})`;
}

function getPhaseLabel(job) {
  if (job.phase === "chunks") return "Uploading files…";
  if (isJobComplete(job)) return "Complete";
  if (job.status === "failed") return "Failed";
  return job.message || "Processing cards…";
}

function BatchUploadJob({ job, onDismiss }) {
  const navigate = useNavigate();
  const isTerminal = isJobComplete(job) || job.status === "failed";

  const goToBatch = () => {
    navigate(`${BATCHES}/${encodeURIComponent(job.batchName)}`);
  };

  return (
    <JobBlock>
      <JobName>{job.batchName}</JobName>
      {isJobComplete(job) ? (
        <SuccessMessage>
          All cards are uploaded.{" "}
          <SuccessLink type="button" onClick={goToBatch}>
            Click here
          </SuccessLink>{" "}
          to view all the cards.
        </SuccessMessage>
      ) : (
        <>
          <JobMeta>
            {getPhaseLabel(job)}
            {job.fileCount > 0 ? ` · ${job.fileCount} files` : ""}
            {" · "}
            {job.progress}%
          </JobMeta>
          <ProgressTrack>
            <ProgressFill
              $width={job.progress}
              $complete={isJobComplete(job)}
              $failed={job.status === "failed"}
            />
          </ProgressTrack>
        </>
      )}
      {job.error && (
        <JobMeta style={{ color: "#b91c1c", marginTop: "0.35rem" }}>
          {job.error}
        </JobMeta>
      )}
      {isTerminal && (
        <FooterActions>
          <TextButton type="button" onClick={() => onDismiss(job.id)}>
            Dismiss
          </TextButton>
        </FooterActions>
      )}
    </JobBlock>
  );
}

export default function BatchUploadProgressPopup() {
  const { jobs, dismissJob } = useBatchUploadProgress();
  const [isMinimized, setIsMinimized] = useState(false);
  const container = usePopupStackContainer();

  if (jobs.length === 0 || !container) return null;

  const activeCount = jobs.filter(
    (j) => !isJobComplete(j) && j.status !== "failed"
  ).length;
  const allTerminal = jobs.every(
    (j) => isJobComplete(j) || j.status === "failed"
  );

  const minimizedLabel = buildMinimizedLabel(jobs, activeCount);
  const stripProgress = getAggregateProgress(jobs);
  const stripFailed = jobs.some((j) => j.status === "failed") && activeCount === 0;
  const stripComplete = jobs.every(isJobComplete);

  const iconProps = { size: 16, strokeWidth: 2.25, "aria-hidden": true };

  return createPortal(
    <PopupShell $minimized={isMinimized}>
      <PopupHeader
        $minimized={isMinimized}
        onClick={() => {
          if (isMinimized) setIsMinimized(false);
        }}
        role={isMinimized ? "button" : undefined}
        tabIndex={isMinimized ? 0 : undefined}
        onKeyDown={(e) => {
          if (isMinimized && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setIsMinimized(false);
          }
        }}
      >
        <HeaderTitle>
          {isMinimized ? minimizedLabel : `Batch upload (${jobs.length})`}
        </HeaderTitle>
        <IconButton
          type="button"
          title={isMinimized ? "Expand" : "Minimize to corner"}
          onClick={(e) => {
            e.stopPropagation();
            setIsMinimized((prev) => !prev);
          }}
        >
          {isMinimized ? (
            <Maximize2 {...iconProps} />
          ) : (
            <Minus {...iconProps} />
          )}
        </IconButton>
        {allTerminal && (
          <IconButton
            type="button"
            title="Close"
            onClick={(e) => {
              e.stopPropagation();
              jobs.forEach((job) => dismissJob(job.id));
            }}
          >
            <X {...iconProps} />
          </IconButton>
        )}
      </PopupHeader>
      {isMinimized && (
        <MinimizedProgressTrack>
          <MinimizedProgressFill
            $width={stripProgress}
            $complete={stripComplete}
            $failed={stripFailed}
          />
        </MinimizedProgressTrack>
      )}
      {!isMinimized && (
        <PopupBody>
          {jobs.map((job) => (
            <BatchUploadJob key={job.id} job={job} onDismiss={dismissJob} />
          ))}
        </PopupBody>
      )}
    </PopupShell>,
    container
  );
}
